import { Headline3, Meta, Spinner, theme } from 'aimo-ui';
import SelectableModalContentItem from 'components/common/SelectableModalContentItem';
import Icon from 'components/icons/Icon';
import useHasUnlockedCustomerSegment from 'hooks/useHasUnlockedCustomerSegment';
import { t } from 'i18next';
import React from 'react';
import { Platform } from 'react-native';
import styled from 'styled-components/native';
import { CenterRow, StyledWrapper } from 'styles/ContainerStyles';
import { ParkingZoneType } from 'types/common';
import {
  AccessMethod,
  AccessType,
  CountryCode,
} from 'types/generatedSchemaTypes';
import { testIdentifiers } from 'utils/testIdentifiers';

const Column = styled.View`
  flex-direction: column;
  flex: 1;
`;

export const BoldMeta = styled(Meta)`
  font-family: ${theme.fontFamily.medium};
  background-color: ${theme.colors.white};
  border: 1px solid #d6dfe3;
  border-radius: 100px;
  padding: 6px 12px;
`;

const RedeemButton = styled.View`
  background-color: white;
  border-radius: 100px;
  padding: 8px 16px;
  border: 1px solid #d6dfe3;
  align-items: center;
  justify-content: center;
  min-width: 90px;
`;

const RedeemText = styled(Meta)`
  color: ${theme.colors.boost.green};
  font-family: ${theme.fontFamily.medium};
  text-align: center;
`;

const Row = styled(CenterRow)`
  flex-grow: 1;
`;

const TariffItem = ({
  tariffPlan,
  isTariffPlanLoading,
  type,
  isSelected,
  zoneCode,
  countryCode,
  onPress,
}: {
  tariffPlan: any;
  isTariffPlanLoading?: boolean;
  type: ParkingZoneType;
  isSelected: boolean;
  countryCode: CountryCode;
  zoneCode?: string;
  onPress: (
    tariffId: string,
    tariffName: string,
    parkingZoneType: ParkingZoneType,
    accessType?: AccessType,
    accessMethod?: AccessMethod,
    isUnlockValid?: boolean
  ) => void;
}) => {
  const { id, name, note, accessType, accessMethod, uid } = tariffPlan;
  const { isUnlockValid } = useHasUnlockedCustomerSegment(
    type === 'CHARGING' ? uid : id,
    countryCode || 'SE',
    zoneCode
  );

  const isLockType = accessType === 'LOCKED';
  const isLocked = isLockType && !isUnlockValid;

  const LockIcon = (
    <Icon
      {...testIdentifiers(
        isLocked ? 'tariff-locked-icon' : 'tariff-unlocked-icon'
      )}
      color={isLocked ? theme.colors.black : theme.colors.boost.green}
      name={isLocked ? 'Locked-Filled' : 'Unlocked-Filled'}
    />
  );

  const CheckboxIcon = (
    <Icon testID="Checkbox" name={isSelected ? 'Checkbox' : 'CheckboxEmpty'} />
  );

  // Prepare comprehensive accessibility label with all tariff information
  const generateAccessibilityLabel = () => {
    let label = name ?? '-';
    if (note) label += `, ${note}`;

    const typeFormatted = type.charAt(0) + type.slice(1).toLowerCase();
    const tariffType = t(`tariff.type${typeFormatted}`);
    label += `, ${tariffType}`;

    if (isLocked) label += `, ${t('tariff.locked')}`;
    if (isSelected) label += `, ${t('common.selected')}`;

    return label;
  };

  return (
    <SelectableModalContentItem
      isSelected={isSelected}
      disabled={false}
      onPress={() =>
        onPress(id, name, type, accessType, accessMethod, isUnlockValid)
      }
      testID={`tariff-wrap-${id}`}
      alignCenter={false}
      notSelectedBorder
      isLocked={isLocked}
      iconNode={isLockType ? LockIcon : CheckboxIcon}
      paddingRight={10}
      itemTitle={name ?? '-'}
      accessibilityLabel={generateAccessibilityLabel()}>
      {isTariffPlanLoading ? (
        <StyledWrapper center>
          <Spinner small />
        </StyledWrapper>
      ) : (
        <Row isVerticalCenter>
          <Column>
            <StyledWrapper mb={theme.normalising.heightPixel(10)}>
              <Headline3 testID={`tariff-name-${name}`}>
                {name || '-'}
              </Headline3>
            </StyledWrapper>
            {note && <Meta testID={`tariff-note-${note}`}>{note}</Meta>}
          </Column>
          {isLocked && (
            <RedeemButton
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('parking.tariff.redeem')}
              aria-label={
                Platform.OS === 'web' ? t('parking.tariff.redeem') : undefined
              }>
              <RedeemText>{t('parking.tariff.redeem')}</RedeemText>
            </RedeemButton>
          )}
        </Row>
      )}
    </SelectableModalContentItem>
  );
};

export default TariffItem;
